import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useNavigate, useLocation } from 'react-router-dom';
import SEO from '../../Components/SEO';
import headingsExercises from './Data/headings-exercises.json';
import Breadcrumbs from '../../Components/Breadcrumb';
import Sidebar from '../../Components/Sidebar';
import './ParagraphHeadings.css'; // New unique CSS

const Headings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [units, setUnits] = useState(headingsExercises);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [evaluation, setEvaluation] = useState([]);
  const [submissionError, setSubmissionError] = useState([]);
  const [currentUnit, setCurrentUnit] = useState(0);
  const [isTopicMenuOpen, setIsTopicMenuOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('all');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const topic = searchParams.get('topic') || 'all';
    setSelectedTopic(topic);
    filterUnits(topic);
  }, [location]);

  const filterUnits = (topic) => {
    const filtered = topic === 'all' 
      ? headingsExercises 
      : headingsExercises.filter(unit => unit.topic === topic);
    setUnits(filtered);
  };

  const toggleTopicMenu = () => setIsTopicMenuOpen(!isTopicMenuOpen);

  const handleTopicChange = (topic) => {
    setSelectedTopic(topic);
    filterUnits(topic);
    setCurrentUnit(0);
    setIsTopicMenuOpen(false);
    resetSelectedChoices();
    navigate(`/reading-exercises/paragraph-headings?topic=${topic}`);
  };

  const handleNextUnit = () => {
    setCurrentUnit(current => (current + 1) % units.length);
    resetSelectedChoices();
  };

  const handlePrevUnit = () => {
    setCurrentUnit(current => (current - 1 + units.length) % units.length);
    resetSelectedChoices();
  };

  const handleChoiceSelect = (index, selectedOption) => {
    const newChoices = [...selectedChoices];
    newChoices[index] = selectedOption ? selectedOption.value : "";
    setSelectedChoices(newChoices);
  };

  const getFilteredOptions = (choices, currentIndex) => {
    // We only exclude choices that are selected in OTHER paragraphs
    const otherSelectedChoices = selectedChoices.filter((choice, idx) => idx !== currentIndex && choice !== "");
    const selectedSet = new Set(otherSelectedChoices);
    
    return choices
      .filter(choice => !selectedSet.has(choice))
      .map(choice => ({ value: choice, label: choice }));
  };

  const handleSubmit = (e, unitIndex, paragraphIndex) => {
    e.preventDefault();
    if (!selectedChoices[paragraphIndex]) {
      const newErrors = [...submissionError];
      newErrors[paragraphIndex] = true;
      setSubmissionError(newErrors);
      return;
    }
    const updatedEval = [...evaluation];
    const isCorrect = selectedChoices[paragraphIndex] === units[unitIndex].paragraphs[paragraphIndex].correctChoice;
    updatedEval[paragraphIndex] = isCorrect ? 'correct' : 'incorrect';
    setEvaluation(updatedEval);
    
    const newErrors = [...submissionError];
    newErrors[paragraphIndex] = false;
    setSubmissionError(newErrors);
  };

  const resetSelectedChoices = () => {
    setSelectedChoices([]);
    setEvaluation([]);
    setSubmissionError([]);
  };

  // Safe check if units exist
  if (!units || units.length === 0) return <div>No exercises found for this topic.</div>;

  return (
    <div className="phe-page-wrapper">
      <SEO
        title='Free English Paragraph Headings Exercises'
        description='Paragraph heading exercises to practice for B2 exams'
        name='English Exam Exercises'
        type='article'
      />
      <Breadcrumbs />

      <header className="phe-intro-section">
        <h3>Paragraph Headings {currentUnit + 1}</h3>
        <div className="phe-details-bar">
            <span className="phe-tag">Level: {units[currentUnit]?.level}</span>
            <span className="phe-tag">Topic: {units[currentUnit]?.topic}</span>
        </div>
        <p>Read the text and match the best heading to each paragraph. There is usually one extra heading you will not need.</p>
      </header>

      <nav className="phe-controls">
        <button className="phe-btn phe-btn-topic" onClick={toggleTopicMenu}>
          <i className="fas fa-filter"></i> Topic: {selectedTopic}
        </button>
        <div className="phe-nav-group">
          <button className="phe-btn" onClick={handlePrevUnit}><i className="fas fa-chevron-left"></i> Previous</button>
          <button className="phe-btn" onClick={handleNextUnit}>Next <i className="fas fa-chevron-right"></i></button>
        </div>
      </nav>

      <div className="phe-content-layout">
        <main className="phe-main-column">
          <h1>{units[currentUnit].headingsTitle}</h1>
          
          {units[currentUnit].paragraphs.map((paragraph, index) => (
            <div key={index} className="phe-paragraph-card">
              <form className="phe-input-row" onSubmit={(e) => handleSubmit(e, currentUnit, index)}>
                <div className="phe-select-wrapper">
                  <Select
                    value={selectedChoices[index] ? { value: selectedChoices[index], label: selectedChoices[index] } : null}
                    onChange={(opt) => handleChoiceSelect(index, opt)}
                    options={getFilteredOptions(paragraph.choices, index)} 
                    placeholder="Select a heading..."
                    classNamePrefix="phe-select"
                    isClearable
                  />
                </div>
                
                <button className="phe-check-btn" type="submit">Check</button>
                
                {evaluation[index] && (
                  <span className="phe-feedback-icon">
                    {evaluation[index] === 'correct' ? '✅' : '❌'}
                  </span>
                )}
                {submissionError[index] && (
                  <span className="phe-feedback-icon" title="Please select an option">⚠️</span>
                )}
              </form>
              <p className="phe-text-content">{paragraph.text}</p>
            </div>
          ))}

          <div className="phe-reset-area">
            <button className="phe-reset-btn" onClick={resetSelectedChoices}>
                <i className="fas fa-undo"></i> Reset All Choices
            </button>
          </div>
        </main>

        <aside className="phe-sidebar-column">
          <Sidebar />
        </aside>
      </div>

      {/* Topic Selection Modal */}
      <div className={`phe-topic-overlay ${isTopicMenuOpen ? 'open' : ''}`} onClick={toggleTopicMenu}>
        <div className="phe-modal" onClick={e => e.stopPropagation()}>
          <h4>Select a Topic</h4>
          <div className="phe-topic-list">
            <button className="phe-topic-btn" onClick={() => handleTopicChange('all')}>All Topics</button>
            <button className="phe-topic-btn" onClick={() => handleTopicChange('Daily')}>Daily Life & Activities</button>
            <button className="phe-topic-btn" onClick={() => handleTopicChange('NoDaily')}>Work & Career</button>
          </div>
          <button style={{marginTop: '20px'}} className="phe-reset-btn" onClick={toggleTopicMenu}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default Headings;
